import { useState, useEffect } from "react";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import * as api from "../../fileUploaderAdminApi";
import FileSchemaEditor from "./FileSchemaEditor";

function SchemaRow({ schema, projectId, onEdit }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
            <Typography variant="body2" fontWeight={500}>{schema.name}</Typography>
          </Box>
        </TableCell>
        <TableCell>
          {/* Schemas are 1:1 with templates — always exactly one template */}
          <Link
            component={RouterLink}
            to={`/projects/${projectId}/file-uploader/admin/templates/${schema.template_id}/edit`}
            variant="body2"
            underline="hover"
          >
            {schema.template_name}
          </Link>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {new Date(schema.created_at).toLocaleDateString()}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Tooltip title="Edit schema">
            <IconButton size="small" onClick={() => onEdit(schema)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Expanded column preview */}
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, border: expanded ? undefined : "none" }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, px: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.50" }}>
                    <TableCell sx={{ width: 40 }}>#</TableCell>
                    <TableCell>Column Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Required</TableCell>
                    <TableCell>Allow Missing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schema.columns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 2 }}>
                        No columns defined.
                      </TableCell>
                    </TableRow>
                  ) : (
                    schema.columns.map((col, idx) => (
                      <TableRow key={col.id ?? col.name}>
                        <TableCell sx={{ color: "text.secondary" }}>{idx + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">{col.name}</Typography>
                        </TableCell>
                        <TableCell><Chip label={col.data_type} size="small" /></TableCell>
                        <TableCell>{col.is_required ? "✓" : "—"}</TableCell>
                        <TableCell>{col.allow_missing ? "✓" : "—"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function SchemasList() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Schema editor drawer state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSchema, setEditingSchema] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [fetchingTemplate, setFetchingTemplate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      setSchemas(await api.fetchSchemas(projectId));
    } catch (err) {
      setError(err.message || "Failed to load schemas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleEditClick = async (schema) => {
    setFetchingTemplate(true);
    setSaveError("");
    try {
      const tmpl = await api.fetchTemplate(schema.template_id);
      setEditingSchema(schema);
      setEditingTemplate(tmpl);
      setEditorOpen(true);
    } catch (err) {
      setError(err.message || "Failed to load schema for editing.");
    } finally {
      setFetchingTemplate(false);
    }
  };

  const handleSchemaEditorSave = async (schemaData) => {
    if (!editingTemplate) return;
    setSaving(true);
    setSaveError("");
    try {
      await api.saveTemplate({
        isEdit: true,
        templateId: editingTemplate.id,
        form: {
          name: editingTemplate.name,
          description: editingTemplate.description,
          schema_name: schemaData.name,
          schema_strict: schemaData.strict,
          file_type_rows: editingTemplate.file_type_rows,
          storage_location_uri: editingTemplate.storage_location_uri,
          storage_location_type: editingTemplate.storage_location_type,
          dataset_name: editingTemplate.dataset_name,
          overwrite_existing: editingTemplate.overwrite_existing,
        },
        columns: schemaData.columns,
        partnerId: editingTemplate.partner_id,
      });
      setEditorOpen(false);
      setEditingSchema(null);
      setEditingTemplate(null);
      load();
    } catch (err) {
      setSaveError(err.message || "Failed to save schema.");
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {schemas.length} schema{schemas.length !== 1 ? "s" : ""}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/projects/${projectId}/file-uploader/admin/templates/new`)}
        >
          New Template
        </Button>
      </Box>

      {fetchingTemplate && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell>Schema Name</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schemas.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No schemas yet. Create a template to get started.
                </TableCell>
              </TableRow>
            )}
            {schemas.map((schema) => (
              <SchemaRow
                key={schema.id}
                schema={schema}
                projectId={projectId}
                onEdit={handleEditClick}
              />
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Schema editor drawer — opens inline without leaving the tab */}
      <FileSchemaEditor
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingSchema(null);
          setEditingTemplate(null);
        }}
        onSave={handleSchemaEditorSave}
        initialSchema={editingSchema}
        overwriteEnabled={editingTemplate?.overwrite_existing ?? false}
        hasUploadedFiles={editingTemplate?.has_uploaded_files ?? false}
        saving={saving}
        saveError={saveError}
      />

    </Box>
  );
}
